import moment from 'moment';

/**
 * The site's overnight floor, night by night.
 *
 * A leak never shows up in a daily total — it hides inside normal variation,
 * and a few cubic metres against ninety is nothing. It shows up as a floor
 * that will not fall to zero when nobody is drawing water. The meters already
 * record this hour by hour; nothing on the dashboard reads it.
 *
 * A rising floor over consecutive nights is the earliest signal these meters
 * can give that something is leaking, stuck open, or drawing unmetered.
 */

/** Hours the plant is treated as idle, in the reporting timezone. */
export const IDLE_FROM_HOUR = 1;
export const IDLE_TO_HOUR = 5;

/** Nights of history the panel reads. */
export const NIGHTS = 14;

/**
 * Groups hourly rows into nights, keeping only the idle window.
 *
 * A night is named for the day it starts on, and the window sits inside one
 * calendar day, so no row has to be reassigned across a midnight boundary.
 *
 * @param {Array<object>} rows - Hourly widget rows carrying `period_ts`.
 * @returns {Map<string, Array<number>>} Night to the values recorded in it.
 */
const nightsFrom = (rows = []) => {
  const nights = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row?.period_ts) return;

    const at = moment(row.period_ts);
    const hour = at.hour();
    if (hour < IDLE_FROM_HOUR || hour > IDLE_TO_HOUR) return;

    const night = at.format('YYYY-MM-DD');
    if (!nights.has(night)) nights.set(night, []);

    nights.get(night).push(Number(row.sum_value) || 0);
  });

  return nights;
};

/**
 * The lowest hourly figure recorded on each night the site was idle.
 *
 * A night whose lowest hour is negative is dropped. Consumption is the rise in
 * a totaliser between one hour and the last, and a totaliser is an odometer —
 * it cannot fall. Both borewells are nonetheless reporting hours where it
 * does, by twelve to sixteen cubic metres, inside the very window this reads.
 * Whatever causes that, the figure is not a floor, and averaging it in put the
 * panel's recent baseline at minus six.
 *
 * The night is dropped rather than clamped to zero, because zero would read as
 * a night with no flow — a real and reassuring finding — when what happened is
 * that the meter cannot be believed.
 *
 * @param {Array<object>} rows - Hourly widget rows.
 * @returns {Array<{night: string, floor: number}>} Nights, oldest first.
 */
export const overnightFloors = (rows = []) =>
  Array.from(nightsFrom(rows).entries())
    .map(([night, values]) => ({ night, floor: Math.min(...values) }))
    .filter((entry) => entry.floor >= 0)
    .sort((a, b) => a.night.localeCompare(b.night));

/**
 * Reads a trend out of those nights.
 *
 * The comparison is the recent nights against the ones before them rather than
 * last night against the night before: a single night moves for ordinary
 * reasons, and what matters is a floor that keeps climbing.
 *
 * @param {Array<object>} rows - Hourly widget rows.
 * @param {number} recent - Nights treated as the recent run.
 * @returns {object|null} The floors, the typical floor of each run and whether
 *   it is climbing, or null when there are too few nights to say.
 */
export const overnightBaseline = (rows = [], recent = 4) => {
  const floors = overnightFloors(rows);
  if (floors.length < recent * 2) return null;

  /**
   * The middle night, not the average one.
   *
   * A night the borewells never ran reads near zero — a real measurement, but
   * of a pump that was off rather than of a floor. Averaging those in with
   * nights the pumps ran drags the earlier figure down and makes any ordinary
   * week look like a rise: on one fortnight of production data three such
   * nights turned a 26 m³/week difference into a claim of 206.
   */
  const middle = (values) => {
    const sorted = [...values].sort((a, b) => a - b);
    const half = Math.floor(sorted.length / 2);

    return sorted.length % 2
      ? sorted[half]
      : (sorted[half - 1] + sorted[half]) / 2;
  };

  const recentFloors = floors.slice(-recent).map((entry) => entry.floor);
  const earlierFloors = floors.slice(0, -recent).map((entry) => entry.floor);

  const recentFloor = middle(recentFloors);
  const earlierFloor = middle(earlierFloors);

  const risingNights = floors
    .slice(1)
    .reduce(
      (run, entry, index) => (entry.floor > floors[index].floor ? run + 1 : 0),
      0
    );

  return {
    floors,
    latest: floors[floors.length - 1].floor,
    recentFloor,
    earlierFloor,
    rising: recentFloor > earlierFloor,
    risingNights,
    weeklyLoss: Math.max(0, recentFloor - earlierFloor) * 24 * 7,
  };
};
